import React from 'react';
import { motion } from 'framer-motion';
import { VideoIcon, DatabaseIcon, RefreshCwIcon, CheckIcon, ZapIcon, CheckCircle2Icon } from 'lucide-react';
export function Hero() {
  const CYCLE_DURATION = 3;
  return <section className="relative min-h-screen pt-24 pb-12 overflow-hidden bg-slate-50 flex flex-col justify-center" data-id="element-139">
      {/* Subtle dot grid background */}
      <div className="absolute inset-0 z-0 opacity-50 pointer-events-none" style={{
      backgroundImage: 'radial-gradient(#94a3b8 1px, transparent 1px)',
      backgroundSize: '24px 24px'
    }} data-id="element-140" />

      <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10" data-id="element-141">
        {/* Headline + Sub-headline */}
        <div className="text-center max-w-3xl mx-auto" data-id="element-142">
          {/* Beta Badge */}
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0
        }} className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-teal-200 bg-teal-50 text-xs font-medium text-teal-700 mb-4" data-id="element-143">
            <div className="w-1.5 h-1.5 rounded-full bg-teal-500" data-id="element-144" />
            Early Access · Beta
          </motion.div>

          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.15
        }} className="text-5xl md:text-6xl lg:text-7xl font-medium text-slate-900 tracking-tight mb-6 text-balance leading-[1.1]" data-id="element-145">
            Eliminate <br data-id="element-146" />
            <span className="text-teal-600" data-id="element-147">post-sales-call admin.</span>
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.25
        }} className="text-lg text-slate-600 font-normal mb-0 max-w-2xl mx-auto leading-relaxed" data-id="element-148">
            Kinase extracts qualification data and next steps from your Zoom
            recordings and syncs everything to HubSpot, automatically.
          </motion.p>
        </div>

        {/* Pipeline Flow Diagram */}
        <div className="mt-10 lg:mt-12 mb-10 lg:mb-12" data-id="element-149">
          <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-0 relative" data-id="element-150">
            {/* Radial teal glow behind the center */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-teal-200/30 rounded-full blur-3xl z-0 pointer-events-none hidden md:block" data-id="element-151" />

            {/* Left group: card + line */}
            <div className="hidden md:flex w-[416px] items-center justify-end relative z-10" data-id="element-152">
              <motion.div initial={{
              opacity: 0,
              x: -30
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 0.5,
              delay: 0.3
            }} className="bg-white border border-slate-200 p-4 rounded-xl flex items-center gap-4 w-64 shadow-md" data-id="element-153">
                <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-154">
                  <VideoIcon className="w-5 h-5 text-blue-500" data-id="element-155" />
                </div>
                <div data-id="element-156">
                  <div className="text-sm font-semibold text-slate-900" data-id="element-157">
                    Zoom Recording
                  </div>
                  <div className="text-xs text-slate-500" data-id="element-158">
                    Vantage Systems · 42m
                  </div>
                </div>
              </motion.div>
              <motion.svg initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.5,
              delay: 0.5
            }} className="w-24 h-8 flex-shrink-0" viewBox="0 0 96 8" fill="none" data-id="element-159">
                <line x1="0" y1="4" x2="96" y2="4" stroke="#cbd5e1" strokeWidth="1.5" data-id="element-160" />
                <motion.circle r="3" fill="#2dd4bf" cy="4" animate={{
                cx: [0, 96]
              }} transition={{
                duration: CYCLE_DURATION,
                repeat: Infinity,
                ease: 'linear'
              }} data-id="element-161" />
              </motion.svg>
            </div>

            {/* Mobile-only: Zoom card */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} className="md:hidden relative z-10" data-id="element-162">
              <div className="bg-white border border-slate-200 p-4 rounded-xl flex items-center gap-4 w-64 shadow-md" data-id="element-163">
                <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-164">
                  <VideoIcon className="w-5 h-5 text-blue-500" data-id="element-165" />
                </div>
                <div data-id="element-166">
                  <div className="text-sm font-semibold text-slate-900" data-id="element-167">
                    Zoom Recording
                  </div>
                  <div className="text-xs text-slate-500" data-id="element-168">
                    Vantage Systems · 42m
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Center: Kinase Circle */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.8
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.5,
            delay: 0.6
          }} className="relative z-10 w-32 h-32 bg-white border-2 border-teal-200 rounded-full flex flex-col items-center justify-center shadow-lg flex-shrink-0" data-id="element-169">
              <motion.div animate={{
              rotate: 360
            }} transition={{
              duration: CYCLE_DURATION,
              repeat: Infinity,
              ease: 'linear',
              delay: 0.6 // Start spinning after it appears
            }} data-id="element-170">
                <RefreshCwIcon className="w-7 h-7 text-teal-500" data-id="element-171" />
              </motion.div>
              <span className="text-[10px] font-bold text-teal-600 tracking-wider mt-1.5" data-id="element-172">
                KINASE
              </span>
            </motion.div>

            {/* Right group: line + card */}
            <div className="hidden md:flex w-[416px] items-center justify-start relative z-10" data-id="element-173">
              <motion.svg initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.5,
              delay: 0.8
            }} className="w-24 h-8 flex-shrink-0" viewBox="0 0 96 8" fill="none" data-id="element-174">
                <line x1="0" y1="4" x2="96" y2="4" stroke="#cbd5e1" strokeWidth="1.5" data-id="element-175" />
                <motion.circle r="3" fill="#f97316" cy="4" animate={{
                cx: [0, 96]
              }} transition={{
                duration: CYCLE_DURATION,
                repeat: Infinity,
                ease: 'linear'
              }} data-id="element-176" />
              </motion.svg>
              <motion.div initial={{
              opacity: 0,
              x: 30
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 0.5,
              delay: 0.9
            }} className="bg-white border border-slate-200 p-5 rounded-xl w-80 shadow-md" data-id="element-177">
                <div className="flex items-center gap-3 mb-4 pb-3 border-b border-slate-100" data-id="element-178">
                  <div className="w-10 h-10 bg-orange-50 rounded-lg flex items-center justify-center" data-id="element-179">
                    <DatabaseIcon className="w-5 h-5 text-orange-500" data-id="element-180" />
                  </div>
                  <div data-id="element-181">
                    <div className="text-sm font-semibold text-slate-900" data-id="element-182">
                      Vantage Systems
                    </div>
                    <div className="text-xs text-slate-500" data-id="element-183">
                      HubSpot Deal Updated
                    </div>
                  </div>
                </div>
                <div className="space-y-2.5" data-id="element-184">
                  <div className="text-[10px] uppercase tracking-wider text-slate-400 font-medium" data-id="element-185">
                    Qualification Data
                  </div>
                  {[{
                  label: 'Budget',
                  value: '$75,000'
                }, {
                  label: 'Authority',
                  value: 'VP Engineering'
                }, {
                  label: 'Need',
                  value: 'Platform migration'
                }, {
                  label: 'Timeline',
                  value: 'Q3 Implementation'
                }].map((item, i) => <div key={i} className="flex items-center justify-between" data-id="element-186">
                      <span className="text-xs text-slate-500" data-id="element-187">
                        {item.label}
                      </span>
                      <span className="text-xs font-medium text-slate-900" data-id="element-188">
                        {item.value}
                      </span>
                    </div>)}
                  <div className="h-px bg-slate-100 my-1" data-id="element-189" />
                  <div className="text-[10px] uppercase tracking-wider text-slate-400 font-medium" data-id="element-190">
                    Next Steps
                  </div>
                  <div className="flex items-center gap-2" data-id="element-191">
                    <CheckCircle2Icon className="w-3.5 h-3.5 text-teal-500 flex-shrink-0" data-id="element-192" />
                    <span className="text-xs text-slate-700" data-id="element-193">
                      Schedule Technical Review
                    </span>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Mobile-only: HubSpot card */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.9
          }} className="md:hidden relative z-10" data-id="element-194">
              <div className="bg-white border border-slate-200 p-5 rounded-xl w-80 shadow-md" data-id="element-195">
                <div className="flex items-center gap-3 mb-4 pb-3 border-b border-slate-100" data-id="element-196">
                  <div className="w-10 h-10 bg-orange-50 rounded-lg flex items-center justify-center" data-id="element-197">
                    <DatabaseIcon className="w-5 h-5 text-orange-500" data-id="element-198" />
                  </div>
                  <div data-id="element-199">
                    <div className="text-sm font-semibold text-slate-900" data-id="element-200">
                      Vantage Systems
                    </div>
                    <div className="text-xs text-slate-500" data-id="element-201">
                      HubSpot Deal Updated
                    </div>
                  </div>
                </div>
                <div className="space-y-2.5" data-id="element-202">
                  <div className="text-[10px] uppercase tracking-wider text-slate-400 font-medium" data-id="element-203">
                    Qualification Data
                  </div>
                  {[{
                  label: 'Budget',
                  value: '$75,000'
                }, {
                  label: 'Authority',
                  value: 'VP Engineering'
                }, {
                  label: 'Need',
                  value: 'Platform migration'
                }, {
                  label: 'Timeline',
                  value: 'Q3 Implementation'
                }].map((item, i) => <div key={i} className="flex items-center justify-between" data-id="element-204">
                      <span className="text-xs text-slate-500" data-id="element-205">
                        {item.label}
                      </span>
                      <span className="text-xs font-medium text-slate-900" data-id="element-206">
                        {item.value}
                      </span>
                    </div>)}
                  <div className="h-px bg-slate-100 my-1" data-id="element-207" />
                  <div className="text-[10px] uppercase tracking-wider text-slate-400 font-medium" data-id="element-208">
                    Next Steps
                  </div>
                  <div className="flex items-center gap-2" data-id="element-209">
                    <CheckCircle2Icon className="w-3.5 h-3.5 text-teal-500 flex-shrink-0" data-id="element-210" />
                    <span className="text-xs text-slate-700" data-id="element-211">
                      Schedule Technical Review
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* CTA + Trust Badges */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 1.1 // Adjusted delay to follow the diagram animation
      }} className="text-center" data-id="element-212">
          <div className="flex items-center justify-center mb-8" data-id="element-213">
            <a href="https://app.getkinase.com" className="px-6 py-3 bg-teal-500 hover:bg-teal-400 text-white rounded-lg font-medium text-base transition-colors flex items-center justify-center space-x-2 shadow-sm shadow-teal-500/20" data-id="element-214">
              <ZapIcon className="w-4 h-4" data-id="element-215" />
              <span data-id="element-216">Request free early access</span>
            </a>
          </div>

          <div className="flex items-center justify-center space-x-5 text-sm text-slate-500" data-id="element-217">
            <div className="flex items-center" data-id="element-218">
              <CheckIcon className="w-4 h-4 mr-1.5 text-teal-500" data-id="element-219" />
              <span data-id="element-220">Free during beta</span>
            </div>
            <span className="w-1 h-1 rounded-full bg-slate-300" data-id="element-221"></span>
            <div className="flex items-center" data-id="element-222">
              <CheckIcon className="w-4 h-4 mr-1.5 text-teal-500" data-id="element-223" />
              <span data-id="element-224">No credit card</span>
            </div>
            <span className="w-1 h-1 rounded-full bg-slate-300" data-id="element-225"></span>
            <div className="flex items-center" data-id="element-226">
              <CheckIcon className="w-4 h-4 mr-1.5 text-teal-500" data-id="element-227" />
              <span data-id="element-228">Setup in minutes</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}